import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgModule,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';

import {
    autoFit,
    autoSizeColumns,
    autoSizeRows,
    bindWidthResize,
    Context,
    DivRenderer,
    DivRendererOptions,
    Grid,
    GridNode,
    RenderContext,
    Row,
    Sort,
    syncCellsToColumns,
} from './experimental.grid';

@Directive({ selector: 'ng-template[gridContent]' })
export class GridContent {
    @Input('data-type') type: 'grid' | 'row' | 'cell';
    @Input('data-id') id: string;

    constructor(public templateRef: TemplateRef<any>) {}
}

export type SelectTemplateFn = (
    context: Context,
    contents: QueryList<GridContent>,
) => GridContent;

@Component({
    selector: '[ngGrid]',
    template: '',
    exportAs: 'grid',
})
@TakeUntilDestroy()
export class ExperimentalNgGridComponent<DataItem = any>
    implements OnChanges, OnInit, AfterContentInit, OnDestroy
{
    @ContentChildren(GridContent)
    private readonly _gridContents: QueryList<GridContent>;

    @Input() rendererOptions: DivRendererOptions;
    @Input() selectTemplate: SelectTemplateFn;
    @Input() grid: Grid;
    @Input() rowsTop: Row[];
    @Input() rows: Row[];
    @Input() dataRows: DataItem[];
    @Input() filteredDataRows: DataItem[];
    @Input() sortDefs: Sort[];
    renderer: DivRenderer;
    gridNode: GridNode;
    @Output() willInflate: EventEmitter<void>;
    @Output() didInflate: EventEmitter<void>;
    private _didContentInit: boolean;
    private _isInflating: boolean;

    constructor(
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this.willInflate = new EventEmitter();
        this.didInflate = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let dataChange = false;
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('dataRows')) {
            // this.rows = JSON.parse(JSON.stringify(this.rows));
            // this.rows = [...this.rows];
            dataChange = true;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('filteredDataRows')) {
            this.rows.forEach((r) => {
                /* cells[n].ctx.data is still referencing original this.data[n] object */
                r.hidden = this.filteredDataRows.every(
                    (e) => e !== r.cells[0].ctx.data,
                );
                r.ctx.index = this.filteredDataRows.findIndex(
                    (e) => e === r.cells[0].ctx.data,
                );
            });
            this.rows.sort((a, b) => a.ctx.index - b.ctx.index);
            if (!dataChange) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._setRows()
                    .then(() => this._onWillInflate())
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => this.renderer.layout())
                    .catch()
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => syncCellsToColumns(this.renderer))
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => this._setSortDefs())
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => this.renderer.layout())
                    .catch()
                    .then(() => this._onDidInflate());
            }
        }

        if (!this._didContentInit) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('rendererOptions') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('selectTemplate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('grid')
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._deflate()
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    return this._inflate();
                });
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        Promise.resolve()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('rowsTop')) {
                    return this._setRowsTop();
                }
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('rows')) {
                    return this._setRows();
                }
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (
                    // eslint-disable-next-line no-prototype-builtins
                    changes.hasOwnProperty('rowsTop') ||
                    // eslint-disable-next-line no-prototype-builtins
                    changes.hasOwnProperty('rows')
                ) {
                    return (
                        this.renderer
                            .layout()
                            .catch()
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            .then(() => syncCellsToColumns(this.renderer))
                    );
                }
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('sortDefs')) {
                    return (
                        this._setSortDefs()
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            .then(() => this.renderer.layout().catch())
                    );
                }
            });
    }

    ngAfterContentInit(): void {
        this._didContentInit = true;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._inflate();
    }

    ngOnInit(): void {
        bindWidthResize(this._el.nativeElement.parentElement)
            .pipe(
                tap(() => {
                    if (
                        this._el.nativeElement.getBoundingClientRect()
                            .height !== 0
                    ) {
                        this._onResize();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.willInflate.complete();
        this.didInflate.complete();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._deflate();
    }

    private _render(context: Context): RenderContext {
        const content = this.selectTemplate
            ? this.selectTemplate(context, this._gridContents)
            : this._gridContents.find((e) => e.type === context.t);
        if (!content || !content.templateRef) {
            return;
        }
        const ref = content.templateRef.createEmbeddedView(context);
        return {
            element: ref.rootNodes[0],
            detectChanges: () => ref.detectChanges(),
            destroy: () => ref.destroy(),
        };
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setRowsTop(): Promise<void> {
        try {
            if (!this._isInflating && this.gridNode && this.rowsTop) {
                return this.gridNode.rowsTop
                    .setChildren(this.rowsTop)
                    .then(() => {});
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setRows(): Promise<void> {
        try {
            if (!this._isInflating && this.gridNode && this.rows) {
                return this.gridNode.setChildren(this.rows).then(() => {});
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setSortDefs(): Promise<void> {
        try {
            if (!this._isInflating && this.gridNode && this.sortDefs) {
                return this.gridNode.sort(this.sortDefs);
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _inflate(): Promise<void> {
        if (!this.rendererOptions || !this._gridContents || this._isInflating) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        const complete = (): Promise<void> => {
            try {
                const el: HTMLElement =
                    this._viewContainerRef.element.nativeElement;
                el.appendChild(this.gridNode.renderContext.element);
            } catch (e) {
                // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
            }
            this._isInflating = false;
            return (
                this._setRowsTop()
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this._setRows();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this.renderer.layout().catch();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this._setSortDefs();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this.renderer.layout().catch();
                    })
                    .then(() => {
                        this._onDidInflate();
                        this.didInflate.emit();
                    })
            );
        };
        this._onWillInflate();
        this.willInflate.emit();
        this._isInflating = true;
        try {
            const rendererOpts: DivRendererOptions = {
                ...this.rendererOptions,
                columns: this.rendererOptions.columns || [], // TODO remove. have underlying grid handle null/unset/missing columns
                defaultRenderFn: (context: Context) => {
                    return this._ngZone.runTask(() => {
                        if (this.rendererOptions.defaultRenderFn) {
                            return this.rendererOptions.defaultRenderFn(
                                context,
                            );
                        }
                        return this._render(context);
                    });
                },
            };
            this.renderer = new DivRenderer(rendererOpts);
            this.gridNode = this.renderer.grid(this.grid || {});
            this.renderer.gridNode = this.gridNode;
            this.renderer.render(this.gridNode);
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return complete();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _deflate(): Promise<void> {
        try {
            const el: HTMLElement =
                this._viewContainerRef.element.nativeElement;
            el.removeChild(this.gridNode.renderContext.element);
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        let prom = Promise.resolve();
        if (this.renderer) {
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            prom = prom.then(() => this.renderer.destroy(this.gridNode));
        }
        return prom.then(() => {
            this.gridNode = null;
            this.renderer = null;
        });
    }

    private _onWillInflate(): void {
        this._el.nativeElement.style.visibility = 'hidden';
    }

    private _onDidInflate(): void {
        // this._changeDetectorRef.detectChanges();
        if (!this.renderer) {
            return;
        }

        // this is dirty. will try to improve the grid api
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.renderer
            .layout()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeColumns(this.rendererOptions.columns, this.renderer),
            )
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoSizeRows(
                    [
                        ...[
                            ...this.renderer.contentColumns.columns.children,
                        ].reverse(),
                        ...this.renderer.rows,
                    ],
                    this.renderer,
                    null,
                    {},
                ),
            )
            .then(() => {
                const left = this.renderer.rowcache.left.filter((e) =>
                    this.gridNode.children.some((ee) => ee.def === e.def),
                );
                left.forEach((e) => {
                    if (e.renderContext?.element != null) {
                        e.renderContext.element.style.height = `${e.def.height}px`;
                    }
                });
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => autoFit(this.renderer))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            .catch((err) => console.warn(err))
            .then(() => {
                this._el.nativeElement.style.visibility = '';
            });
    }

    private _onResize(): void {
        this._onWillInflate();
        this._onDidInflate();
    }
}

@NgModule({
    declarations: [GridContent, ExperimentalNgGridComponent],
    exports: [GridContent, ExperimentalNgGridComponent],
})
export class ExperimentalNgGridModule {}
